import React, { useEffect, useState, lazy, Suspense } from "react";
import { useHistory } from 'react-router-dom';
import { SwiperSlide } from 'swiper/react/swiper-react.js';
import {isNil} from 'ramda'

// @material-ui
import { Box, Typography, Button, Icon } from "@material-ui/core";
import { Alert } from '@material-ui/lab';
import Hidden from '@material-ui/core/Hidden';
import Link from '@material-ui/core/Link';
import Collapse from '@material-ui/core/Collapse';

// @sdk
import { ProgramService, MembershipService } from "@sdk-point/talisis";

// @shared
import Loader from '../../../shared/components/Loader/LoaderContent';
import { useCoursesContext } from '../../../shared/components/CourseCarousel/context/CoursesProvider';
import { types } from '../../../shared/components/CourseCarousel/context/CoursesReducer';
import TitleLink from '../../../shared/components/TitleLink/TitleLink';
import { goToCourseOfAlly } from "../../../shared/utils/goToCourseOfAlly.js";
import { validateMembership, redirectMembershipView, resolveProgramAccessAction } from "../../../shared/utils/membershipUtils";
import { ACCESS_ACTION } from "../../../design-system/constants/access-action";
import { convertSourceToImageResource } from "../../../constants/placeholderImages.js";
import { CoursesValidators } from "../../../shared/utils/classValidator";

import SlickCarousel from '../../../shared/components/SlickCarousel/SlickCarousel';
import AcademiesSection from "../CoursesInProgress/AcademiesSection/AcademiesSection";
import BannerNotCourses from "../CoursesInProgress/components/BannerNotCourses.jsx";
import OnStartMyClasses from "../CoursesInProgress/components/OnStartMyClasses.jsx";
import { getSessionCookie, setSessionCookie } from "../../../shared/utils/Sessions";
import { MEMBERSHIPS } from "../../../shared/constants/memberships";
import { log } from '../../../shared/utils/console.js';

// @styles
import useStyles from '../../../assets/styles/jss/pages/courses/coursesInProgressStyle';

// Cart
import { manuallyAttemptMembershipPayment as attemptMembershipPayment } from "../../../shared/utils/membershipRepurchase.js";

// @Redux
import { applyCart, applyCartProducts } from "../../../redux/actions/cartActions.js";

// Roles
import { hasRole } from "../../../services/RoleService";
import { ROLE_ACADEMY } from "../../../shared/constants/roles";
import { useDispatch, useSelector } from "react-redux";
import { CART } from "../../../routes/paths";

// Design-system
import StatusCourseCard from "../../../design-system/components/Cards/StatusCourseCard/StatusCourseCard.jsx";
import { getSourceBySourceAndCompany } from "../../../design-system/utils/source.js";

const CourseEnrollAsyncClass = lazy(() => import('../CoursesInProgress/components/CourseEnrollAsyncClass'));

const MembershipServiceSDK = new MembershipService(process.env.REACT_APP_ENV, process.env.REACT_APP_X_API_KEY);
const ProgramServiceSDK = new ProgramService(process.env.REACT_APP_ENV, process.env.REACT_APP_X_API_KEY);

const ProgramsInProgress = () => {
    const history = useHistory();
    const classes = useStyles();
    const session = getSessionCookie();
    const { dispatch } = useCoursesContext();
    const dispatchCart = useDispatch();
    const { user } = useSelector((state) => state.userReducer);
    const { cart } = useSelector((state) => state.cartReducer);

    const [programsInProgress, setProgramInProgress] = useState({ async_programs: [], sync_programs: [] });
    const [userMembership, setUserMembership] = useState(null);
    const [nextMembership, setNextMembership] = React.useState(null);
    const [userAddOn, setUserAddOn] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingRepurchase, setLoadingRepurchase] = useState(false);
    const [membershipRenovationCopyOpen, setMembershipRenovationCopyOpen] = useState(false);

    useEffect(() => {
        getAllData();
        setMembershipRenovationCopyOpen(true);
    }, []);

    const getAllData = async () => {
        setLoading(true);

        const programData = await getProgramsInProgress();
        setProgramInProgress(() => programData);

        const personMembershipDetail = await MembershipServiceSDK.getPersonMembershipDetail(user.person_id);
        setUserMembership(personMembershipDetail.userMembership);
        setNextMembership(isNil(personMembershipDetail.nextMembership) ? personMembershipDetail.userMembership:personMembershipDetail.nextMembership);
        setSessionCookie({ ...session, membership_id: personMembershipDetail?.userMembership?.id, membership_type_id: personMembershipDetail?.userMembership?.membership_type_id });
        setUserAddOn(personMembershipDetail.userAddOn);

        setLoading(false);
    };

    const getProgramsInProgress = async () => {
        let response = { async_programs: [], sync_programs: [] };

        try {
            response = await ProgramServiceSDK.getProgramsByPersonId(user.person_id);
            dispatch({ type: types.ADD_PROGRAMS, payload: response.async_programs });

            // Aplicar validación de prerrequisitos a cada programa async
            const validatedPrograms = await Promise.all(
                response.async_programs.map(async (program) => {
                    try {
                        const [validated] = await CoursesValidators.blockers([program]);
                        return validated;
                    } catch (err) {
                        log('[ProgramsInProgress] blockers falló para programa', program.program_id, err);
                        return program;
                    }
                })
            );
            response = { ...response, async_programs: validatedPrograms };
        } catch (error) {
            log('Error fetch programs by person id', error);
        }

        return response;
    };

    const handleChangePage = (e, type_resource, id, student) => {
        e.preventDefault();
        student = (type_resource === 'programas') ? `/${student}` : '';
        history.push(`/${type_resource}/${id}/cursos${student}`);
    };

    const handleCloseMembershipRenovationMessage = () => {
        setMembershipRenovationCopyOpen(false);
    };

    const manuallyAttemptMembershipPayment = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        await attemptMembershipPayment({
            userMembership: nextMembership,
            userAddOn,
            cart,
            user,
            dispatchCart,
            applyCart,
            applyCartProducts,
            history,
            cartPath: CART,
            setLoadingRepurchase
        });
    };

    const levels = programsInProgress.async_programs.map(it => it.level_id);

    // Si el userMembership.id está en algún membresia_id de los programas → renovacion
    // Si no está → upgrade
    const isRenovationMembership = [...programsInProgress.async_programs, ...programsInProgress.sync_programs]
        .some(program => Array.isArray(program.membresia_id) && program.membresia_id.includes(userMembership?.id));
    
    return (
        <React.Fragment>
            <OnStartMyClasses user={user} levels={levels} />
            {!loading && (userMembership && userMembership.id > MEMBERSHIPS.FREEMIUM && (!userMembership.is_active || userMembership?.subscription_past_due)) &&
                <Collapse in={membershipRenovationCopyOpen}>
                    <Loader loading={loadingRepurchase} transparent={true} width="100%">
                        <Alert variant="filled" severity="info" color="info" icon={<i className="ri-error-warning-fill text-warning-400"></i>} className="mt-3">
                            <Typography component="div" variant="body2" className="semi-bold">Desafortunadamente no pudimos realizar el cobro para la {isRenovationMembership? 'renovación':'reactivar'} de tu membresía.</Typography>
                            <Box display="flex">
                                <Typography component="p" variant="body2" className="mt-2 text-warning-100" style={{ width: '90%' }}>
                                    Para {isRenovationMembership? 'renovación':'reactivar'} tu suscripción <Link href="" variant="body2" className="text-orange-300" onClick={(e)=> redirectMembershipView(e, dispatchCart, history)}>Haz clic aquí</Link>. Si ya realizaste tu pago <Link href="#" variant="body2" className="text-orange-300" onClick={() => {
                                        const talisisHelpDeskButton = document.getElementById('talisis_helpdesk_button');
                                        if (talisisHelpDeskButton) {
                                            talisisHelpDeskButton.click();
                                        }
                                    }}>Contáctanos</Link> para que nuestro equipo de soporte te ayude a {isRenovationMembership? 'renovación':'reactivar'} tu membresía.
                                </Typography>
                                <Typography component="p" align="right" style={{ width: '10%' }}>
                                    <Button className={classes.closeMembershipAlertButton} onClick={handleCloseMembershipRenovationMessage} endIcon={<i className="ri-close-line font-size-10"></i>}>CERRAR</Button>
                                </Typography>
                            </Box>
                        </Alert>
                    </Loader>
                </Collapse>
            }
            <Loader loading={loading} transparent={true} minHeight="290px" width={!loading ? "100%" : "95%"} style={{ marginLeft: "auto", marginRight: "auto" }}>
                {!loading && programsInProgress.async_programs.length === 0 && programsInProgress.sync_programs.length === 0 && <BannerNotCourses />}
                <Box className={classes.root}>
                    <Box position="relative" mt={2}>
                        {
                            programsInProgress.async_programs.map((program) => {
                                return (<Box position="relative" className={[classes.hover_carousel, "mb-4"]} key={program.program_id}>
                                    <Typography variant="overline" className={"overline-small text-gray-500"}>Programa inscrito:</Typography>
                                    {
                                        !program.courses.length ?
                                        <>
                                            <Box display='flex' justifyContent='space-between' alignItems='center' className='flex-xs-column align-items-xs-end'>
                                                <Typography variant="h3">{program.program_name}</Typography>
                                                <Hidden xsDown>
                                                    <Button variant='contained' onClick={(e) => handleChangePage(e, 'programas', program.program_id, false)}>Ver todas las materias</Button>
                                                </Hidden>
                                                <Hidden smUp>
                                                    <Button color='primary'
                                                        endIcon={<Icon className="ri-arrow-right-s-line" />}
                                                        onClick={(e) => handleChangePage(e, 'programas', program.program_id, false)}>Ver todos</Button>
                                                </Hidden>
                                            </Box>
                                            {
                                                program.remaining_credits === 0 &&
                                                userMembership?.id !== MEMBERSHIPS.FREEMIUM &&
                                                program.days_until_next_credit !== '' && (
                                                    <Box mt={2}>
                                                        <Typography variant="body1" className="text-gray-300">
                                                            {(() => {
                                                                const hasDays = program.days_until_next_credit !== undefined &&
                                                                    program.days_until_next_credit !== null;
                                                                const creditsMessage = hasDays
                                                                    ? `Tus nuevos créditos se activarán en ${program.days_until_next_credit} ${program.days_until_next_credit === 1 ? 'día' : 'días'}.`
                                                                    : 'Tus nuevos créditos se activarán pronto.';
                                                                return (
                                                                    <>
                                                                        <Box component="div" mb={1}>
                                                                            <strong>¡Tu avance no se detiene! 🚀💖</strong>
                                                                        </Box>
                                                                        <Box component="div" mb={1}>
                                                                            Aún no se liberan tus materias del mes.
                                                                        </Box>
                                                                        <Box component="div" mb={1}>
                                                                            <strong>{creditsMessage}</strong>
                                                                        </Box>
                                                                        <Box component="div" mb={1}>
                                                                            Mientras tanto, date un respiro, recarga energía y prepárate para continuar con todo el ánimo cuando tus nuevas materias estén disponibles.
                                                                        </Box>
                                                                        <Box component="div">
                                                                            <strong>¡Lo estás haciendo excelente! 💪✨</strong>
                                                                        </Box>
                                                                    </>
                                                                );
                                                            })()}
                                                        </Typography>
                                                    </Box>
                                                )
                                            }
                                            {
                                                program.remaining_credits === 0 &&
                                                <BannerNotCourses />
                                            }
                                        </>
                                        : <Typography className={classes.title} variant="h3"><TitleLink textPrimary={program.program_name} className={classes.titlesin} textSecondary="Ver materias" onClick={(e) => handleChangePage(e, 'programas', program.program_id, false)} /></Typography>
                                    }
                                    <Box display={"flex"} gridGap={16} mb={0.5}><Typography variant="overline" className={[classes.title, 'variant-overline semibold text-blue-300']}>Créditos disponibles: {program.remaining_credits}</Typography> <Icon className="ri-information-line text-warning-300 font-size-16" /> </Box>
                                    <SlickCarousel infinite="false" length={program.courses.length} typeCard="normal" slidestoshow={1} slidestoscroll={1} variablewidth='true' slidemargin="0px">
                                        {
                                            program.courses.map((courseItem) => {
                                                const membershipExpired =
                                                    (userMembership?.id === MEMBERSHIPS.FREEMIUM || userMembership?.remaining_days_before_expired < -8)
                                                        ? true
                                                        : validateMembership(userMembership);
                                                const accessAction = resolveProgramAccessAction({
                                                    membresiaIds: program.membresia_id,
                                                    userMembership,
                                                    membershipExpired,
                                                });

                                                return (<SwiperSlide key={courseItem?.course_id} className={classes.card}>
                                                    <Suspense fallback={<div></div>}>
                                                        <StatusCourseCard
                                                            courseId={courseItem.course_id}
                                                            sourceId={courseItem.source_id}
                                                            userId={parseInt(user.person_id ?? 0)}
                                                            sourceIdLogo={getSourceBySourceAndCompany(courseItem.source_id, courseItem.company_id)}
                                                            enrollmentStatusId={courseItem?.enrollment_status_id || 3}
                                                            media={{
                                                                backgroundImage: courseItem?.image_url || convertSourceToImageResource(courseItem.company_id, courseItem.source_id),
                                                                modalityId: courseItem?.modality_id || 2,
                                                                percentage: courseItem?.progress_percentage,
                                                                grade: courseItem?.grade,
                                                            }}
                                                            content={{
                                                                courseName: courseItem?.course_name,
                                                                grade: courseItem?.progress_percentage === 100 ? courseItem?.grade : false,
                                                            }}
                                                            companyId={courseItem?.company_id}
                                                            onClickGoToCourse={() =>
                                                                goToCourseOfAlly({
                                                                    person_id: user.person_id,
                                                                    email: user.email,
                                                                    ou: program.program_id,
                                                                    course_url: courseItem?.course_url,
                                                                    source_id: courseItem?.source_id,
                                                                    course_url_neo: courseItem?.course_url_neo,
                                                                    course_id: courseItem?.course_id,
                                                                    company_id: courseItem?.company_id,
                                                                    course_name: courseItem?.course_name,
                                                                    setLoad: setLoading,
                                                                    eventSource: 'inProgram'
                                                                })
                                                            }
                                                            isLogged={true}
                                                            approved={courseItem?.progress_percentage === 100 ? (courseItem?.approved ?? false) : null}
                                                            onClickRegister={(e) => handleChangePage(e, 'programas', program.program_id, false)}
                                                            showDetailButton={false}
                                                            blocked={courseItem?.blocked}
                                                            blockedReason={courseItem?.blockedReason}
                                                            blockedByCourseName={courseItem?.blockedByCourseName}
                                                            handleRenovationMembership={(e) => redirectMembershipView(e, dispatchCart, history)}
                                                            onClickUpgrade={(e) => redirectMembershipView(e, dispatchCart, history)}
                                                            accessAction={accessAction}
                                                            merbershipIsExpired={accessAction !== ACCESS_ACTION.ENROLL}
                                                        />
                                                    </Suspense>
                                                </SwiperSlide>
                                                )
                                            })
                                        }
                                        {
                                            (program.remaining_credits > 0) && <SwiperSlide key='enroll-class' className={classes.card}>
                                                <Suspense fallback={<div></div>}>
                                                    <CourseEnrollAsyncClass {...program} />
                                                </Suspense>
                                            </SwiperSlide>
                                        }
                                    </SlickCarousel>
                                </Box>
                                )
                            })
                        }
                    </Box>
                    {/* Programas Sincrónicos */}
                    <Box position="relative" mt={2}>
                        {
                            programsInProgress.sync_programs.map((program) => {
                                return (<Box position="relative" className={[classes.hover_carousel, "mb-4"]} key={program.program_id}>
                                    <Typography variant="overline" className={"overline-small text-gray-500"}>Programa inscrito:</Typography>
                                    <Typography className={classes.title} variant="h3">
                                        <TitleLink textPrimary={program.program_name} className={classes.titlesin} textSecondary="Ver materias" onClick={(e) => handleChangePage(e, 'programas', program.program_id, false)} />
                                    </Typography>
                                    <SlickCarousel infinite="false" length={program.courses.length} typeCard="normal" slidestoshow={1} slidestoscroll={1} variablewidth='true' slidemargin="0px">
                                        {
                                            program.courses.map((courseItem) => {
                                                const accessAction = resolveProgramAccessAction({
                                                    membresiaIds: program.membresia_id,
                                                    userMembership,
                                                    membershipExpired: validateMembership(userMembership),
                                                });

                                                return (<SwiperSlide key={courseItem?.course_id} className={classes.card}>
                                                    <Suspense fallback={<div></div>}>
                                                        <StatusCourseCard
                                                            courseId={courseItem.course_id}
                                                            sourceId={courseItem.source_id}
                                                            userId={parseInt(user.person_id ?? 0)}
                                                            sourceIdLogo={getSourceBySourceAndCompany(courseItem.source_id, courseItem.company_id)}
                                                            enrollmentStatusId={courseItem?.enrollment_status_id || 3}
                                                            media={{
                                                                backgroundImage: courseItem?.image_url || convertSourceToImageResource(courseItem.company_id, courseItem.source_id),
                                                                modalityId: courseItem?.modality_id || 2,
                                                                percentage: courseItem?.progress_percentage,
                                                                grade: courseItem?.grade,
                                                            }}
                                                            content={{
                                                                courseName: courseItem?.course_name,
                                                                grade: courseItem?.progress_percentage === 100 ? courseItem?.grade : false,
                                                            }}
                                                            companyId={courseItem?.company_id}
                                                            onClickGoToCourse={() =>
                                                                goToCourseOfAlly({
                                                                    person_id: user.person_id,
                                                                    email: user.email,
                                                                    ou: program.program_id,
                                                                    course_url: courseItem?.course_url,
                                                                    source_id: courseItem?.source_id,
                                                                    course_url_neo: courseItem?.course_url_neo,
                                                                    course_id: courseItem?.course_id,
                                                                    company_id: courseItem?.company_id,
                                                                    course_name: courseItem?.course_name,
                                                                    setLoad: setLoading,
                                                                    eventSource: 'inProgram'
                                                                })
                                                            }
                                                            isLogged={true}
                                                            approved={courseItem?.progress_percentage === 100 ? (courseItem?.approved ?? false) : null}
                                                            showDetailButton={false}
                                                            handleRenovationMembership={(e) => redirectMembershipView(e, dispatchCart, history)}
                                                            onClickUpgrade={(e) => redirectMembershipView(e, dispatchCart, history)}
                                                            accessAction={accessAction}
                                                            merbershipIsExpired={accessAction !== ACCESS_ACTION.ENROLL}
                                                        />
                                                    </Suspense>
                                                </SwiperSlide>
                                                )
                                            })
                                        }
                                    </SlickCarousel>
                                </Box>
                                )
                            })
                        }
                    </Box>
                </Box>
            </Loader>
        </React.Fragment>
    );
};

export default ProgramsInProgress;
